import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardBody, Form, Badge, Input, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import SessionContext from "SessionContext";
import ProgressLoader from 'partials/progress-loader';
import './census-sync.styl';

export default function DestinationObjectSelector({ sourceId, destinationId, destinationName, selectedObject: parentSelectedObject, hasMappingChanges, onSelect, onBack, onRefresh }) {
  const session = useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(parentSelectedObject || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, pendingObject: null });
  const [confirmRefreshModal, setConfirmRefreshModal] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await session.command(`census/refresh-destination-object?census_destination_id=${destinationId}`);
      await pollForDestinationRefresh(response.refresh_key);
    } catch (err) {
      console.error("Error refreshing destination objects:", err);
    } finally {
      setIsRefreshing(false);
      getDestinationObjects();
      onRefresh?.();
    }
  }

  const pollForDestinationRefresh = async (refreshKey, interval = 1000, timeout = 30000) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const response = await session.query('census/refresh-destination-object-status', {census_destination_id: destinationId, refresh_key: refreshKey});
      if (response === 'completed') {
        return response;
      }
      await new Promise(r => setTimeout(r, interval));
    }
    throw new Error("Destination refresh status polling timed out");
  }

  const getDestinationObjects = () => {
    session.query('census/get-destination-objects', { census_destination_id: destinationId })
      .then((response) => {
        response.sort((a, b) => {
          const re = /(\d+)|(\D+)/g;
          const aParts = a.label.match(re);
          const bParts = b.label.match(re);
          for(let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i] || '';
            const bPart = bParts[i] || '';
            if(aPart !== bPart) {
              const aNum = Number(aPart);
              const bNum = Number(bPart);
              if(!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              return aPart.localeCompare(bPart);
            }
          }
          return 0;
        });
        setObjects(response || []);
        // If there's a selected object, try to find it in the refreshed list
        if (selectedObject && response?.length > 0) {
          const refreshedObject = response.find(obj => obj.full_name === selectedObject.full_name);
          if (refreshedObject) {
            // Re-select with updated object data from refresh
            setSelectedObject(refreshedObject);
            onSelect(refreshedObject);
          } else {
            // Selected object no longer exists, auto-select first
            setSelectedObject(response[0]);
            onSelect(response[0]);
          }
        } else if (!selectedObject && response?.length > 0) {
          // Auto-select first object if none selected
          setSelectedObject(response[0]);
          onSelect(response[0]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching destination objects:", err);
      });
  }

  useEffect(() => {
    if(!destinationId) { return; }
    getDestinationObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[destinationId]);

  return (
    <Card >
      <>
        <CardHeader>
          <div className="sync-refresh">
            <h3><strong>1.</strong><span>Select a Destination Object</span>{destinationName && <span>for: <Badge color="success">{destinationName}</Badge></span>}</h3>
            {!isRefreshing && (
              <Button color="link" size="sm" onClick={() => {
                if (hasMappingChanges) {
                  setConfirmRefreshModal(true);
                  return;
                }
                handleRefresh();
              }} disabled={isRefreshing} className="btn-link-census">
                <i className="bi bi-arrow-clockwise" /> refresh destination
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
        {(loading || isRefreshing) && <ProgressLoader />}
        <Form>
          <>
            <div className="destination-object-search">
              <InputGroup>
                <InputGroupText>
                  <i className="bi bi-search" />
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search destination objects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="button-select destination-object-list">
              {objects
                ?.filter((object) =>
                  object.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    object.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((object) => (
                  <Button
                    outline
                    key={object.full_name}
                    color={selectedObject?.full_name === object.full_name ? "primary" : "light"}
                    className={`select-item ${selectedObject?.full_name === object.full_name ? 'active' : ''}`}
                    onClick={() => {
                      // Check if selecting a different object while there are mapping changes
                      if (hasMappingChanges && selectedObject?.full_name !== object.full_name) {
                        setConfirmModal({ open: true, pendingObject: object });
                        return;
                      }
                      setSelectedObject(object);
                      onSelect(object);
                    }}
                  >
                    <i className={`bi ${selectedObject?.full_name === object.full_name ? 'bi-check-circle' : 'bi-circle'}`} />
                    {object.label}
                  </Button>
                ))}
              {objects?.filter((object) =>
                object.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  object.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
              ).length === 0 && (
                  <p className="text-muted">No matching destination objects found</p>
                )}
            </div>
          </>
        </Form>

        <Modal centered backdrop isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false, pendingObject: null })}>
          <ModalHeader toggle={() => setConfirmModal({ open: false, pendingObject: null })}>
            <i className="bi bi-exclamation-triangle me-2" />
            Confirm Change
          </ModalHeader>
          <ModalBody>
            This change will cause a loss of field mapping changes. Do you want to continue?
          </ModalBody>
          <ModalFooter>
            <Button className="promote" onClick={() => setConfirmModal({ open: false, pendingObject: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const object = confirmModal.pendingObject;
                setConfirmModal({ open: false, pendingObject: null });
                setSelectedObject(object);
                onSelect(object);
              }}
            >
              Continue
            </Button>
          </ModalFooter>
        </Modal>

        <Modal centered backdrop isOpen={confirmRefreshModal} toggle={() => setConfirmRefreshModal(false)}>
          <ModalHeader toggle={() => setConfirmRefreshModal(false)}>
            <i className="bi bi-exclamation-triangle me-2" />
            Confirm Refresh
          </ModalHeader>
          <ModalBody>
            Refreshing destinations will cause a loss of field mapping changes. Do you want to continue?
          </ModalBody>
          <ModalFooter>
            <Button className="promote" onClick={() => setConfirmRefreshModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmRefreshModal(false);
                handleRefresh();
              }}
            >
              Continue
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
      </>
    </Card>
  )
}
